import { capitalize } from "lodash-es";

import {
  Flow,
  FlowVersionFlowChild,
  FlowVersionStatus,
} from "src/api/flowTypes";
import { useFlows } from "src/api/queries";
import { ConfirmationModal } from "src/base-components/ConfirmationModal";
import { ExternalLink } from "src/base-components/ExternalLink";
import { Pill } from "src/base-components/Pill";
import {
  FeatureMappingOutput,
  PreviewQueryRequestEnvironmentEnum,
} from "src/clients/features-control";
import { Accordion, useAccordionSingleMode } from "src/design-system/Accordion";
import { useAllJobs } from "src/jobs/api/queries";
import { Job } from "src/jobs/types";
import { FEATURE_FLAGS, isFeatureFlagEnabled } from "src/router/featureFlags";
import { useWorkspaceContext } from "src/router/routerContextHooks";
import {
  getBaseUrl,
  getUrlToAuthoringPage,
  getUrlToJobPage,
} from "src/router/urls";
import {
  entityNameToJsonRefPath,
  eventNameToJsonRefPath,
} from "src/schema/jsonRefUtils";

type BaseProps = {
  isOpen: boolean;
  closeModal: () => void;
  onConfirm: () => void;
};

type Props =
  | (BaseProps & {
      isActive: true;
      environment: PreviewQueryRequestEnvironmentEnum;
      entities: string[];
      event: string;
      features: FeatureMappingOutput[];
    })
  | (BaseProps & {
      isActive: false;
      features: FeatureMappingOutput[];
    });

export const QueryToggleConfirmationModal = (props: Props) => {
  const { isOpen, closeModal, onConfirm, isActive } = props;

  const featureLifecycleEnabled = isFeatureFlagEnabled(
    FEATURE_FLAGS.featureLifecycle,
  );

  if (!isOpen) {
    return null;
  }

  if (isActive) {
    return (
      <DeactivateConfirmationModal
        closeModal={closeModal}
        entities={props.entities}
        environment={props.environment}
        event={props.event}
        features={props.features}
        isOpen={isOpen}
        onConfirm={onConfirm}
      />
    );
  }

  return (
    <ConfirmationModal
      confirmationButtonText="Activate"
      description={
        <div className="flex flex-col gap-y-4 font-inter-normal-13px">
          <span>
            Once you activate, you might not be able to edit the Query nor its
            associated Features. Are you sure you want to activate this Query?
          </span>
          {featureLifecycleEnabled && (
            <UsedFeaturesList features={props.features} />
          )}
        </div>
      }
      open={isOpen}
      title="Activate Query"
      variant="default"
      onClose={closeModal}
      onConfirm={onConfirm}
    />
  );
};

const DeactivateConfirmationModal: React.FC<{
  isOpen: boolean;
  closeModal: () => void;
  onConfirm: () => void;
  environment: PreviewQueryRequestEnvironmentEnum;
  entities: string[];
  event: string;
  features: FeatureMappingOutput[];
}> = ({
  isOpen,
  closeModal,
  onConfirm,
  environment,
  entities,
  event,
  features,
}) => {
  const { workspace, orgId } = useWorkspaceContext();

  const featureLifecycleEnabled = isFeatureFlagEnabled(
    FEATURE_FLAGS.featureLifecycle,
  );

  const { data: flows, isLoading: isFlowsLoading } = useFlows({
    workspaceId: workspace.id,
    includeSchemas: true,
  });

  const { data: jobs, isLoading: isJobsLoading } = useAllJobs(
    workspace.base_url!,
    {
      statuses: ["active"],
    },
  );

  const refsToCheck = new Set([
    ...entities.map(entityNameToJsonRefPath),
    eventNameToJsonRefPath(event),
  ]);

  const usedVersions = flows.flatMap((flow) =>
    flow.versions
      .filter(
        (version) =>
          version.status === FlowVersionStatus.PUBLISHED &&
          version?.input_schema?.properties &&
          hasMatchingRef(version.input_schema.properties, refsToCheck),
      )
      .map((version) => ({ flow, version })),
  );

  const usedJobs =
    jobs?.filter(
      (job) =>
        job.active_source?.configuration?.type === "entity" &&
        entities.includes(job.active_source.configuration.entity_schema),
    ) || [];

  const disableConfirmationButton =
    featureLifecycleEnabled && (isJobsLoading || isFlowsLoading);

  return (
    <ConfirmationModal
      confirmationButtonText="Deactivate"
      description={renderDeactivateDescription(
        environment,
        usedVersions,
        orgId,
        workspace.id,
        usedJobs,
        features,
        isFlowsLoading,
        isJobsLoading,
      )}
      disableConfirmationButton={disableConfirmationButton}
      disableConfirmationButtonTooltip="Loading affected resources..."
      open={isOpen}
      title="Deactivate Query?"
      variant="danger"
      onClose={closeModal}
      onConfirm={onConfirm}
    />
  );
};

const hasMatchingRef = (
  properties: Record<
    string,
    { $ref?: string; anyOf?: { $ref?: string }[]; oneOf?: { $ref?: string }[] }
  >,
  refsToCheck: Set<string>,
) => {
  return Object.values(properties).some(
    (property) =>
      (property.$ref != null && refsToCheck.has(property.$ref)) ||
      (property.anyOf?.some((v) => v.$ref != null && refsToCheck.has(v.$ref)) ??
        false) ||
      (property.oneOf?.some((v) => v.$ref != null && refsToCheck.has(v.$ref)) ??
        false),
  );
};

const CollapsibleResourceList: React.FC<{
  title: string;
  resources: { id: string; name: string; url?: string }[];
  value: string;
  isLoading?: boolean;
}> = ({ title, resources, value, isLoading }) => {
  const { value: accordionValue, onChange } = useAccordionSingleMode();

  return (
    <Accordion value={accordionValue} onChange={onChange}>
      <Accordion.Item
        title={
          isLoading ? `Loading ${title}...` : `${resources.length} ${title}`
        }
        value={value}
      >
        <ul className="decideScrollbar max-h-40 list-inside list-disc overflow-y-auto">
          {resources.map((resource) => (
            <li key={resource.id}>
              {resource.url ? (
                <ExternalLink href={resource.url}>{resource.name}</ExternalLink>
              ) : (
                <span>{resource.name}</span>
              )}
            </li>
          ))}
        </ul>
      </Accordion.Item>
    </Accordion>
  );
};

const renderDeactivateDescription = (
  environment: PreviewQueryRequestEnvironmentEnum,
  usedVersions: {
    flow: Flow;
    version: FlowVersionFlowChild;
  }[],
  orgId: string,
  wsId: string,
  usedJobs: Job[],
  features: FeatureMappingOutput[],
  isFlowsLoading: boolean,
  isJobsLoading: boolean,
) => {
  const featureLifecycleEnabled = isFeatureFlagEnabled(
    FEATURE_FLAGS.featureLifecycle,
  );

  const isLive = environment === PreviewQueryRequestEnvironmentEnum.LIVE;
  if (featureLifecycleEnabled) {
    const baseUrl = getBaseUrl();
    return (
      <div className="flex flex-col gap-y-4 font-inter-normal-13px">
        <span>
          {`This Query and the Features generated by it are currently active on
          ${capitalize(environment)} data. Deactivating might break the Decision Flows and Jobs that
          use them.`}
        </span>
        <UsedFeaturesList features={features} />
        {isLive && (
          <>
            <CollapsibleResourceList
              isLoading={isFlowsLoading}
              resources={usedVersions.map(({ flow, version }) => ({
                id: version.id,
                name: `${flow.name} ${version.name}`,
                url: `${baseUrl}${getUrlToAuthoringPage(orgId, wsId, flow.id, version.id)}`,
              }))}
              title="Published Decision Flow(s)"
              value="flows"
            />
            <CollapsibleResourceList
              isLoading={isJobsLoading}
              resources={usedJobs.map((job) => ({
                id: job.id,
                name: job.name,
                url: `${baseUrl}${getUrlToJobPage(orgId, wsId, job.flow_id, job.id)}`,
              }))}
              title="Active Job(s)"
              value="jobs"
            />
          </>
        )}
      </div>
    );
  } else {
    return "Deactivating this feature query will halt all associated feature value computations. This may affect published flows that rely on these feature values for their logic.";
  }
};

const UsedFeaturesList: React.FC<{ features: FeatureMappingOutput[] }> = ({
  features,
}) => (
  <div className="flex flex-wrap gap-2">
    {features.map((feature) => (
      <Pill key={feature.feature_key} size="sm" variant="gray">
        <Pill.Text>{feature.feature_key}</Pill.Text>
      </Pill>
    ))}
  </div>
);
